<template>
  <div class="py-16 lg:py-20">
    <div class="container px-4 mx-auto sm:px-6 lg:px-8">
      <div class="mb-8 text-center">
        <h2 class="mb-2 text-3xl font-bold text-gray-800">
          {{ t("services.title") }}
        </h2>
        <p class="text-lg text-gray-600">
          {{ t("services.subtitle") }}
        </p>
      </div>

      <!-- Navigation Controls -->
      <div
        class="flex items-center justify-center gap-4 mb-6"
        @mouseenter="pauseAutoplay"
        @mouseleave="resumeAutoplay"
      >
        <button
          @click="prevSlide"
          class="p-2 text-gray-600 transition-colors rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          aria-label="Previous slide"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <div class="flex gap-2">
          <button
            v-for="(_, index) in Math.ceil(services.length / itemsPerSlide)"
            :key="index"
            @click="currentSlide = index"
            class="w-3 h-3 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            :class="currentSlide === index ? 'bg-indigo-600' : 'bg-gray-300'"
            :aria-label="`Go to slide ${index + 1}`"
          ></button>
        </div>

        <button
          @click="nextSlide"
          class="p-2 text-gray-600 transition-colors rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          aria-label="Next slide"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      <!-- Slider Container -->
      <div
        class="relative overflow-hidden"
        @mouseenter="pauseAutoplay"
        @mouseleave="resumeAutoplay"
      >
        <div
          class="flex transition-transform duration-500 ease-in-out"
          :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
        >
          <!-- Slide Groups -->
          <div
            v-for="slideIndex in Math.ceil(services.length / itemsPerSlide)"
            :key="slideIndex - 1"
            class="grid flex-shrink-0 w-full grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-6"
          >
            <!-- Service Cards for this slide -->
            <template
              v-for="(service, serviceIndex) in services.slice(
                (slideIndex - 1) * itemsPerSlide,
                slideIndex * itemsPerSlide
              )"
              :key="serviceIndex"
            >
              <a
                :href="service.url"
                target="_blank"
                class="h-full transition duration-300 transform hover:scale-105"
                @mouseenter="pauseAutoplay"
                @mouseleave="resumeAutoplay"
              >
                <div
                  class="flex flex-col h-full overflow-hidden bg-white rounded-lg shadow-sm hover:shadow-md"
                >
                  <div :class="`h-1 sm:h-2 bg-${service.color}-600`"></div>
                  <div class="flex flex-col flex-grow p-3 sm:p-4">
                    <div
                      class="flex items-center justify-center h-10 mb-2 sm:h-12 md:h-16 sm:mb-3"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        :class="`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-${service.color}-600`"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          :d="service.icon"
                        />
                      </svg>
                    </div>

                    <h3
                      class="mb-1 text-sm font-semibold text-center text-gray-800 sm:mb-2 sm:text-base md:text-lg line-clamp-1"
                    >
                      {{ service.title }}
                    </h3>
                    <p
                      class="flex-grow text-xs text-center text-gray-600 sm:text-sm line-clamp-2"
                    >
                      {{ service.description }}
                    </p>
                    <div class="flex justify-center mt-2 sm:mt-3 md:mt-4">
                      <span
                        :class="`inline-flex items-center text-[10px] sm:text-xs font-medium text-${service.color}-600`"
                      >
                        {{ t("services.accessNow") }}
                        <svg
                          class="w-2 h-2 ml-1 sm:w-3 sm:h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          ></path>
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";

export default {
  props: {
    jenisdokumen: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },
  data() {
    return {
      currentSlide: 0,
      itemsPerSlide: 6,
      autoplayInterval: null,
      isPaused: false,
    };
  },
  computed: {
    services() {
      // Menggunakan data dari props.jenisdokumen
      if (!this.jenisdokumen || !this.jenisdokumen.data) {
        return [];
      }

      // Warna dan ikon default yang bisa dirotasi
      const colors = ["yellow", "blue", "green", "red", "indigo", "purple"];
      // Ikon dokumen untuk semua item
      const documentIcon =
        "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z";

      // Menggunakan properti id, nama, dan deskripsi dari data jenisdokumen
      return this.jenisdokumen.data.map((item, index) => {
        return {
          id: item.id,
          title: item.nama || "",
          description: item.deskripsi || "",
          url: item.url || item.link || "#",
          color: colors[index % colors.length],
          icon: documentIcon,
        };
      });
    },
    totalSlides() {
      return Math.ceil(this.services.length / this.itemsPerSlide);
    },
    currentServices() {
      const start = this.currentSlide * this.itemsPerSlide;
      return this.services.slice(start, start + this.itemsPerSlide);
    },
  },
  mounted() {
    this.startAutoplay();
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeUnmount() {
    this.stopAutoplay();
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
    },
    prevSlide() {
      this.currentSlide =
        (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    },
    startAutoplay() {
      this.stopAutoplay();
      if (!this.isPaused) {
        this.autoplayInterval = setInterval(() => {
          this.nextSlide();
        }, 5000); // Change slide every 5 seconds
      }
    },
    stopAutoplay() {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    },
    pauseAutoplay() {
      this.isPaused = true;
      this.stopAutoplay();
    },
    resumeAutoplay() {
      this.isPaused = false;
      this.startAutoplay();
    },
    handleResize() {
      // Adjust items per slide based on screen size
      if (window.innerWidth < 640) {
        this.itemsPerSlide = 2; // Small Mobile: 2 items
      } else if (window.innerWidth < 768) {
        this.itemsPerSlide = 4; // Mobile: 4 items
      } else if (window.innerWidth < 1024) {
        this.itemsPerSlide = 6; // Tablet: 6 items
      } else {
        this.itemsPerSlide = 6; // Desktop: 6 items
      }

      // Make sure current slide is valid after resize
      if (this.currentSlide >= this.totalSlides) {
        this.currentSlide = 0;
      }
    },
  },
};
</script>

<style scoped>
/* Optional: Add custom animation styles */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.5s ease;
}
.slide-enter-from {
  transform: translateX(100%);
}
.slide-leave-to {
  transform: translateX(-100%);
}

/* Responsive card adjustments */
@media (min-width: 1024px) {
  .grid-cols-6 > a {
    min-height: 220px;
  }

  /* Add a subtle hover effect */
  a.hover\:scale-105:hover {
    box-shadow: 0 5px 15px -3px rgba(0, 0, 0, 0.1),
      0 5px 5px -3px rgba(0, 0, 0, 0.04);
    transform: translateY(-3px) scale(1.02);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
}

/* Mobile card adjustments */
@media (max-width: 640px) {
  .grid-cols-2 > a {
    min-height: 150px;
  }
}
</style>
